/** Abrir o que foi partilhado comigo, de outro workspace.
 *
 *  - um modelo: as revisões vêm pela partilha
 *    (`GET /api/modules/modelos/partilha/{id}`) e abre-se a mais recente,
 *    como um IFC local (com a mesma verificação do SHA-256);
 *  - um BCF: cria-se aqui um BCF vazio ligado à partilha, que se
 *    sincroniza por `partilha:<id>` e traz os tópicos da casa dele;
 *  - uma triagem: o banco dos conflitos entra nela (ver
 *    `ClashPartilhaBarra`, que consome o pedido de abertura).
 */
import { useAppStore } from '@core/store'
import { abrirModeloPartilhado } from '@core/modelos/modelosPartilhados'
import { modelosApi } from '@core/modelos/modelosApi'
import type { ItemComigo } from './partilhasRegras'
import { pedirAbertura } from './partilhasEstado'

export async function abrirModeloDaPartilha(item: ItemComigo): Promise<boolean> {
  const toast = useAppStore.getState().pushToast
  try {
    const revisoes = await modelosApi.revisoesDaPartilha(item.id)
    const ultima = revisoes.find((r) => r.ultima) ?? revisoes[0]
    if (!ultima) {
      toast(`"${item.nome ?? 'Modelo'}" já não tem revisões disponíveis.`, { type: 'info' })
      return false
    }
    return (await abrirModeloPartilhado(ultima)) !== 'falhou'
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Não foi possível abrir o modelo.', { type: 'error' })
    return false
  }
}

/** O BCF local ligado a esta partilha (criado se ainda não existir), já
 *  activo. Devolve o id local. */
export function abrirBcfDaPartilha(item: ItemComigo): string {
  const st = useAppStore.getState()
  const existente = st.bcfLoaded.find((b) => b.partilhaId === item.id)
  if (existente) {
    st.bcfSetActive(existente.localId)
    return existente.localId
  }
  const novo = st.bcfCreateEmpty(item.nome ?? 'BCF partilhado', {
    sourceProjectId: item.chave ?? null,
    partilhaId: item.id,
  })
  st.bcfSetActive(novo.localId)
  return novo.localId
}

/** A triagem abre-se no banco dos conflitos: abre-o e deixa o pedido,
 *  que a barra da triagem partilhada consome quando estiver pronta. */
export function abrirTriagemDaPartilha(item: ItemComigo): void {
  pedirAbertura(item)
  useAppStore.getState().setClashPanelOpen(true)
}
